/**
 * The one global stylesheet for the web build.
 *
 * Everything in here is something react-native styles cannot express: pseudo
 * classes (:focus-visible), document-level defaults, native form chrome and
 * scrollbars. Component sizing stays in the component wrappers and in
 * webTokens.ts - this file deliberately does *not* try to restyle screens.
 *
 * It is injected once from index.web.js. Android never loads it.
 */

import {
  webColor,
  webControl,
  webFontSize,
  webFontStack,
  webLineHeight,
  webModal,
  webZIndex,
} from './webTokens';

const STYLE_ELEMENT_ID = 'zenbasket-pos-web-ui';

function buildCss(): string {
  return `
/* ------------------------------------------------------------------ *
 * Tokens exposed to CSS. Same numbers as webTokens.ts - declared here so
 * rules below (and anything that needs them later) read from one place.
 * ------------------------------------------------------------------ */
:root {
  --zen-font-stack: ${webFontStack};
  --zen-font-size-body: ${webFontSize.body}px;
  --zen-font-size-input: ${webFontSize.input}px;
  --zen-font-size-error: ${webFontSize.error}px;
  --zen-line-height-normal: ${webLineHeight.normal};
  --zen-control-height: ${webControl.height}px;
  --zen-control-radius: ${webControl.borderRadius}px;
  --zen-color-primary: ${webColor.primary};
  --zen-color-focus: ${webColor.focusRing};
  --zen-color-focus-soft: ${webColor.focusRingSoft};
  --zen-color-border: ${webColor.border};
  --zen-color-border-hover: ${webColor.borderHover};
  --zen-color-autofill: ${webColor.autofill};
  --zen-color-text: ${webColor.text};
  --zen-color-error: ${webColor.error};
  --zen-color-overlay: ${webColor.overlay};
  --zen-z-dropdown: ${webZIndex.dropdown};
  --zen-z-popover: ${webZIndex.popover};
  --zen-z-modal: ${webZIndex.modal};
  --zen-z-toast: ${webZIndex.toast};
  --zen-z-tooltip: ${webZIndex.tooltip};
}

/* ------------------------------------------------------------------ *
 * 13.5 / 13.6 - one font family, everywhere.
 *
 * ZenText sets fontFamily explicitly, so text that goes through it is already
 * Montserrat. Text inputs are not: react-native-web's base style for both
 * TextInput and Text is the CSS shorthand \`font: 14px System\`, and the
 * shorthand resets the family. RNW then expands "System" into
 *
 *   -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial
 *
 * (createReactDOMStyle.js), so on Windows every input that does not name a
 * family of its own renders in Segoe UI while the label above it renders in
 * Montserrat. That is the "some screens use a different font" report, and it
 * shows up wherever a raw TextInput is used - ZenDropdown's search box,
 * ZenSearchBar, and the handful of bare <TextInput>s in the screens.
 * ------------------------------------------------------------------ */
html,
body,
#root {
  font-family: var(--zen-font-stack);
  font-size: var(--zen-font-size-body);
  line-height: var(--zen-line-height-normal);
  color: var(--zen-color-text);
}

/* Native form chrome ignores inherited font by spec, RNW class or not. */
input,
textarea,
select,
button,
optgroup,
option {
  font-family: var(--zen-font-stack);
  font-size: inherit;
  line-height: inherit;
}

/*
 * Same declaration at one step higher specificity (element + attribute beats
 * the single class RNW emits), which is what actually displaces the System
 * stack on a react-native-web TextInput.
 *
 * Only the family is forced - never the size - so per-screen font sizes keep
 * working exactly as they do today. The one thing this would override is an
 * input deliberately set in Montserrat-SemiBold; there are none in the app
 * (verified across src/), and a bold *input* is not part of the design
 * language. If one is ever added, give it a class of its own rather than
 * weakening this rule.
 */
input[class],
textarea[class],
select[class] {
  font-family: var(--zen-font-stack);
}

/* Date/number pickers render their own popup UI with UA fonts. */
::-webkit-datetime-edit,
::-webkit-calendar-picker-indicator {
  font-family: var(--zen-font-stack);
}

/* ------------------------------------------------------------------ *
 * 13.3 - focus behaviour.
 *
 * The "black rectangle" is the UA focus ring Chrome paints on a focused
 * element that has no outline of its own. Rather than removing focus
 * indication (which would break keyboard navigation), both states get an
 * explicit ring: a quiet one for pointer focus, a strong one for keyboard
 * focus. An outline is painted outside the box model, so neither ring can
 * move the layout - that is the whole reason for using outline here rather
 * than a border or box-shadow inset.
 * ------------------------------------------------------------------ */
input,
textarea,
select,
[contenteditable='true'] {
  caret-color: var(--zen-color-primary);
}

/*
 * A ZenInput renders a bordered wrapper around a bare <input>, and the
 * wrapper is padded - measured on Create Customer, the wrapper is
 * 120,227 705x42 while the input inside it is 129,228 687x40. Ringing the
 * input therefore paints a second rectangle that is inset on the left and
 * sits exactly on top of the field border top and bottom: the doubled
 * border people see when they click into a field.
 *
 * So the ring goes on the field box instead, found through the marker the
 * web ZenInput wrapper puts on its input. :has() matches the direct parent
 * only, so this can never escape onto a layout container.
 */
input[data-zen-field]:focus,
textarea[data-zen-field]:focus {
  outline: none;
}

div:has(> input[data-zen-field]:focus),
div:has(> textarea[data-zen-field]:focus) {
  outline: 2px solid var(--zen-color-focus-soft);
  outline-offset: 0;
}

div:has(> input[data-zen-field]:focus-visible),
div:has(> textarea[data-zen-field]:focus-visible) {
  outline: 2px solid var(--zen-color-focus);
  outline-offset: 0;
}

/*
 * Pointer affordance on the field box. react-native has no hover state, so
 * fields gave no feedback at all until they were clicked. Only the border
 * colour changes - no size, no shadow, nothing that could move the layout.
 *
 * !important is needed here and nowhere else: react-native-web writes
 * dynamic style values straight onto the element, so the field box carries
 * style="border-color: rgb(221,221,221)" inline, and an inline declaration
 * outranks any selector. The rule is narrow - a hovered, unfocused ZenInput
 * box - so the override cannot reach anything else.
 */
div:has(> input[data-zen-field]:hover:not(:focus)),
div:has(> textarea[data-zen-field]:hover:not(:focus)) {
  border-color: var(--zen-color-border-hover) !important;
}

/*
 * 14.1 / 14.2 / 14.8 / 14.10 - the same promotion, for field boxes that are
 * not the input's direct parent.
 *
 * Every search bar in the app - zen-core's ZenSearchBar, the sell screen's
 * CustomSearchBar, the Create Customer phone row - is a bordered, rounded
 * wrapper containing a *borderless* TextInput, and CustomSearchBar puts one
 * more <View> in between for its fake placeholder. So the :has(> ...) rule
 * above cannot reach the box, and the generic fallback below was ringing the
 * bare input instead: a second rectangle inset a few pixels inside the
 * rounded container. That is the doubled border reported on the Sell Screen
 * search, the Create Customer phone field, and the Brand/Product list
 * searches - one cause, four screenshots.
 *
 * The web search wrapper marks its container with data-zen-field-box, which
 * makes the anchor explicit instead of structural, so the ring lands on the
 * visible field at any nesting depth. Descendant :has() is safe here because
 * the marker is only ever put on the box itself.
 */
[data-zen-field-box]:has(input[data-zen-field]:focus),
[data-zen-field-box]:has(textarea[data-zen-field]:focus) {
  outline: 2px solid var(--zen-color-focus-soft);
  outline-offset: 0;
}

[data-zen-field-box]:has(input[data-zen-field]:focus-visible),
[data-zen-field-box]:has(textarea[data-zen-field]:focus-visible) {
  outline: 2px solid var(--zen-color-focus);
  outline-offset: 0;
}

/*
 * When an explicit field box exists, the input's direct parent is an inner
 * layout wrapper - CustomSearchBar puts one there so it can overlay a faked
 * placeholder - and the depth-1 rule further up would ring *that*, which is
 * the inset rectangle all of this is meant to remove. Measured on the Sell
 * Screen search: box at 92,103 646x40 and wrapper at 132,114 593x18, both
 * outlined. The box wins; the descendant combinator means this can never
 * match the box itself, so a search bar whose input is a direct child of the
 * box keeps its ring.
 */
[data-zen-field-box] div:has(> input[data-zen-field]:focus),
[data-zen-field-box] div:has(> textarea[data-zen-field]:focus) {
  outline: none;
}

/* Same pointer affordance as a ZenInput, same reason for !important. */
[data-zen-field-box]:has(input[data-zen-field]:hover:not(:focus)),
[data-zen-field-box]:has(textarea[data-zen-field]:hover:not(:focus)) {
  border-color: var(--zen-color-border-hover) !important;
}

/* Everything else keeps a ring on the control itself. */
input:not([data-zen-field]):focus,
textarea:not([data-zen-field]):focus,
select:focus,
[contenteditable='true']:focus {
  outline: 2px solid var(--zen-color-focus-soft);
  outline-offset: 0;
  border-radius: var(--zen-control-radius);
}

:focus-visible:not([data-zen-field]) {
  outline: 2px solid var(--zen-color-focus);
  outline-offset: 1px;
  border-radius: var(--zen-control-radius);
}

/* Pointer focus on non-text controls stays silent; keyboard focus does not. */
[role='button']:focus:not(:focus-visible),
button:focus:not(:focus-visible),
div[tabindex]:focus:not(:focus-visible) {
  outline: none;
}

/* Touch-screen POS terminals: kill the grey flash, keep the focus ring. */
* {
  -webkit-tap-highlight-color: transparent;
}

/* ------------------------------------------------------------------ *
 * 13.7 - button hover.
 *
 * react-native has no hover state, so ZenButton has none either: pressed is
 * covered by activeOpacity, disabled by its own styles and focus by the
 * rules above, but a pointer over a button gave no feedback at all. The
 * web button wrapper tags each rendered button with data-zen-button (via
 * dataSet), which keeps this scoped to real buttons instead of every
 * role=button in the app - list rows and icon touchables must not light up.
 * ------------------------------------------------------------------ */
[data-zen-button]:hover {
  filter: brightness(0.93);
}

[data-zen-button][aria-disabled=true]:hover,
[data-zen-button]:disabled:hover {
  filter: none;
  cursor: not-allowed;
}

/* ------------------------------------------------------------------ *
 * 13.13 - remove the built-in sources of layout shift.
 * ------------------------------------------------------------------ */

/*
 * Number spinners appear on hover and take horizontal space away from the
 * value, which reshuffles a price/quantity row the moment the pointer passes
 * over it. POS quantity fields are driven by the keypad and the scanner.
 */
input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/*
 * Chrome's autofill restyles the control, and the restyle lands on the wrong
 * box.
 *
 * A ZenInput is a bordered wrapper around a bare <input>, and the wrapper is
 * padded: measured on the sign-in email field, the box is 90,405 572x42 while
 * the input inside it is 99,406 554x40. Chrome paints its autofill background
 * on the *input*, so the fill stops 9px short of the border on each side - an
 * inset rectangle sitting inside the field rather than filling it. On the
 * sign-in screen, where the field is transparent on a dark teal panel, that
 * inset block is impossible to miss.
 *
 * So the fill is promoted to the field box, exactly as the focus ring and the
 * hover border already are, and the input is filled to match so the two read
 * as one surface. The text colour has to be set explicitly with it: the
 * sign-in field's own text is white for the dark panel, which would vanish
 * against the autofilled background.
 *
 * !important on the box for the usual reason - react-native-web writes
 * background-color inline, and an inline declaration outranks any selector.
 */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--zen-color-text);
  -webkit-box-shadow: 0 0 0 1000px var(--zen-color-autofill) inset;
  caret-color: var(--zen-color-text);
  font-family: var(--zen-font-stack) !important;
  font-size: var(--zen-font-size-input) !important;
}

div:has(> input[data-zen-field]:-webkit-autofill),
[data-zen-field-box]:has(input[data-zen-field]:-webkit-autofill) {
  background-color: var(--zen-color-autofill) !important;
}

/*
 * Overlay scrollbars on a scroll container reserve no space until the content
 * overflows, so a list that grows past its box shoves its own contents
 * sideways. A stable gutter keeps the column width fixed either way.
 */
[data-zen-stable-scrollbar='true'] {
  scrollbar-gutter: stable;
}

/* ------------------------------------------------------------------ *
 * 13.9 / 13.10 - one overlay and one stacking scale.
 *
 * react-native-web renders <Modal> as a portalled, position:fixed node
 * carrying aria-modal="true". Anchoring that node to the shared modal layer
 * is what stops a dropdown or a toast from being painted through an open
 * dialog. The overlay's own colour stays where it already is (the screens'
 * appColors.transparentBackground) - only stacking is centralised.
 * ------------------------------------------------------------------ */
/*
 * body-qualified so the selector outscores the atomic class react-native-web
 * emits for the modal root - measured at z-index 0 without it. In practice the
 * portal is the last child of <body> so it already paints on top; this makes
 * the declared layer true rather than incidental.
 */
body [aria-modal='true'] {
  z-index: var(--zen-z-modal);
}

/*
 * 15.4 - a dialog must never grow past the window.
 *
 * The sell screen builds around a dozen dialogs on one shared card, and that
 * card sizes to its content with no ceiling of any kind. Most of them are
 * short, but a long one - a discount list, a payment breakdown - simply grows
 * off the bottom of the browser window and takes its footer buttons with it.
 *
 * This is a stylesheet rule rather than a react-native style because
 * overflow:auto does not exist in react-native: the only values it accepts are
 * visible, hidden and scroll. Hidden would clip the footer instead of moving
 * it, and scroll would paint a scrollbar on every short dialog. auto is the
 * one that does what the spec asks - scroll only when there is something to
 * scroll.
 */
[data-zen-modal-card] {
  max-height: 90vh;
  overflow-y: auto;
}

/*
 * An open dropdown list must paint over the form below it.
 *
 * react-native-web emits every View as position:relative with z-index:0
 * (View/index.js:134), which makes every View a stacking context. A
 * descendant can therefore never raise itself past its own parent, so the
 * list's own zIndex of 5000 is trapped, and since every sibling View is
 * z-index 0 too, document order decides - whatever is written after the
 * dropdown paints on top of it. Measured on the customer form: nine
 * consecutive ancestors, all position relative, all z-index 0.
 *
 * Raising the list alone cannot work for that reason. The whole branch has
 * to be raised, which is what the descendant :has() does here: it matches
 * every ancestor of the open dropdown, at any depth, and lifts each one
 * above its own siblings. The dropdown's branch then wins at every level.
 *
 * It costs nothing when no dropdown is open - the marker only exists while
 * one is - and it cannot reach a modal or a toast, which are portalled out
 * to <body> on higher layers than this one.
 */
div:has([data-zen-dropdown-open='true']),
[data-zen-dropdown-open='true'] {
  z-index: var(--zen-z-dropdown) !important;
}
/* react-native-toast-message mounts last and must clear the modal layer. */
[data-zen-layer='toast'] {
  z-index: var(--zen-z-toast);
}

/* ------------------------------------------------------------------ *
 * Scrollbars - cosmetic consistency across the app's many scroll areas.
 * ------------------------------------------------------------------ */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
::-webkit-scrollbar-track {
  background: ${webColor.scrollbarTrack};
}
::-webkit-scrollbar-thumb {
  background-color: ${webColor.scrollbarThumb};
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: content-box;
}

/*
 * 16.11 - 16.13: hide the bar, keep the scrolling.
 *
 * react-native-web already emits scrollbar-width: none for a scroll view
 * that asks to hide its indicator, and current Chrome and Edge honour that.
 * Older Chromium does not, and reaches for ::-webkit-scrollbar instead -
 * which the scrollbar block above has given a 10px width, putting the bar
 * straight back. Both spellings are answered here so the result is the same
 * in every supported browser.
 *
 * This only removes the bar. overflow stays exactly as it was, so the wheel,
 * the trackpad and the keyboard all still scroll the area.
 */
[data-zen-hide-scrollbar='true'] {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

[data-zen-hide-scrollbar='true']::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

/* ------------------------------------------------------------------ *
 * Placeholder keys - a pressable with nothing in it and no fill.
 *
 * The PIN keypad pads its last row with an empty key (pin.tsx: null in the
 * numbers list). The shared buttonTouchable style gives every key an iOS
 * shadow*, and the screen switches it off for that key with elevation: 0 -
 * which is Android-only. react-native-web ignores elevation but turns the
 * shadow* props into box-shadow, so on web the invisible key still drew its
 * shadow and read as a blank white button beside 0.
 *
 * An element that is empty *and* transparent has nothing to show but that
 * shadow, so dropping it can never hide real content. The background test
 * keeps filled empty pressables (colour swatches and the like) untouched.
 * ------------------------------------------------------------------ */
[tabindex]:empty[style*='background-color: rgba(0, 0, 0, 0)'] {
  box-shadow: none !important;
}

/* ------------------------------------------------------------------ *
 * Overlay icon buttons must be clickable.
 *
 * A screen places an icon button over its layout with position: absolute -
 * the PIN screen's back arrow (pin.tsx) is written before the two panes it
 * floats over. react-native-web makes every view position: relative, so those
 * later panes stack *above* the earlier absolute button in DOM order and
 * swallow the click; the arrow was visible but dead. On Android the same tree
 * delivers the touch to the arrow.
 *
 * Matched narrowly: a focusable, absolutely positioned element whose only
 * child is a single text node (an icon glyph or a label). z-index 1 lifts it
 * over its in-flow siblings only - it stays inside its own stacking context,
 * so modals and menus with their own z-index still sit above it, and an
 * explicit inline zIndex on the button still wins.
 * ------------------------------------------------------------------ */
[tabindex][style*='position: absolute']:has(> [dir='auto']:only-child) {
  z-index: 1;
}

/* ------------------------------------------------------------------ *
 * Quick Edit (products) - card collapsing to a narrow column.
 *
 * Every other dialog puts its card (modifierSetStyle.innerView) straight into
 * the full-width modal shell, so the card's width: 60% is 60% of the
 * screen. quickProductEdit.tsx wraps the card in one more KeyboardAvoidingView
 * with no width. The shell centres its children, so on web that wrapper
 * shrinks to fit its content, and a percentage of a shrink-to-fit box
 * collapses: measured 158px of a 264px wrapper, with the Cancel/Update row
 * spilling out below the card. Android lays the same tree out full width.
 *
 * Stretching the wrapper and centring the card inside it restores the
 * intended geometry: 60% / 80% / 90% of the screen, capped by
 * webOverrides.web.ts at webModal.maxWidth.
 *
 * Matched through the card itself: max-width 960px is the web cap every
 * dialog card gets from webOverrides, and overflow: visible is set only by
 * Quick Edit (so its dropdowns can open past the card edge). No other dialog
 * matches.
 * ------------------------------------------------------------------ */
div:has(> [style*='max-width: ${webModal.maxWidth}px'][style*='overflow: visible']) {
  align-self: stretch;
  align-items: center;
}

/*
 * Same wrapper, vertical axis: the card's web height cap (maxHeight 90%,
 * webOverrides) is a percentage of *this* wrapper - which is only as tall as
 * the card - so the card clipped itself to 90% of its own height and cut the
 * Cancel/Update row off (450 of 500px). Measured against the viewport it is
 * the same cap the other dialogs get from the full-height shell. !important
 * because the percentage is written inline.
 */
[style*='max-width: ${webModal.maxWidth}px'][style*='overflow: visible'] {
  max-height: ${Math.round(webModal.maxHeightRatio * 100)}vh !important;
}

/* ------------------------------------------------------------------ *
 * Print - the receipt iframe owns the printed page (printer.web.ts).
 * ------------------------------------------------------------------ */
@media print {
  #root {
    display: none;
  }
}
`;
}

/**
 * Injects the stylesheet. Safe to call more than once (hot reload re-runs the
 * entry module), and a no-op outside a browser.
 */
export function installGlobalStyles(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const existing = document.getElementById(STYLE_ELEMENT_ID);
  if (existing) {
    return;
  }

  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.appendChild(document.createTextNode(buildCss()));

  /*
   * Prepended, not appended: react-native-web injects its atomic classes into
   * the head at runtime, and those must be able to win over these defaults.
   * Screen-level styles therefore keep overriding the baseline, exactly as
   * they do today.
   */
  const head = document.head;
  if (head.firstChild) {
    head.insertBefore(style, head.firstChild);
  } else {
    head.appendChild(style);
  }
}
